<template>
	<div class="discountEvent h-full w-full rounded-sm bg-black">
		<TimelineEvent
			:event="event"
			:class="[
				`discountEvent discountEvent__${event.event_origin} discountEvent__opportunity-${fromOpportunityType}`,
				{
					'cursor-pointer': isClickable && !readonly,
					'cursor-default': !isClickable || readonly,
					'is-selected': isSelected,
					discountEvent__curated: event.is_curated_discount
				}
			]"
			:style="portalColorVar"
			:tooltip-max-width="650"
			:simplified="simplified"
		>
			<template #body>
				<div class="flex items-center overflow-hidden">
					<div
						v-if="shouldShowWarning || (!simplified && ((portal && isFromStoreOpportunity) || isFromCalculatedOpportunity))"
						class="mr-02 flex items-center gap-02"
					>
						<WarningIcon v-if="shouldShowWarning" class="eventEntry--icon min-w-05" />

						<PortalIcon v-if="portal && !simplified && isFromStoreOpportunity" :portal="portal" class="icon--size-1" />

						<Icon v-if="!simplified && isFromCalculatedOpportunity" name="sale" />
					</div>

					<span class="overflow-hidden text-ellipsis whitespace-nowrap">
						<template v-if="!simplified"> {{ event.name }} </template>

						<!-- In simplified mode show depth instead of event name -->
						<template v-else>{{ event.discount_depth }}%</template>
					</span>
				</div>
			</template>

			<!-- In simplified mode do not show anything in the depthSuffix slot -->
			<template v-if="simplified" #depthSuffix><span>&nbsp;</span></template>

			<template #tooltipTitleContent>
				<div class="flex items-center">
					<WarningIcon v-if="isNonOptimal" class="mr-02" />

					<span>{{ title }}</span>
				</div>
			</template>

			<template #tooltipBody>
				<TooltipBodyTemplate :event="event">
					<template v-if="isPossiblyNotLegalNOAEvent" #footer>
						<div class="mt-05 flex gap-03">
							<WarningIcon class="mr-02" />
							<span class="max-w-[26rem]"
								>This event was created before the latest rule change. It may not comply with the current rules and could be invalid or
								rejected by Nintendo.
							</span>
						</div>
					</template>
				</TooltipBodyTemplate>
			</template>
		</TimelineEvent>
	</div>
</template>

<script setup lang="ts">
import { PortalIcon, Icon } from '@bluebrick/saas-ui'
import { computed, toRefs } from 'vue'
import WarningIcon from '@/common/components/WarningIcon.vue'
import { StoreToPortalMap } from '@/common/utils'
import TimelineEvent from '@/features/events/components/timeline/events/TimelineEvent.vue'
import TooltipBodyTemplate from '@/features/events/components/timeline/events/TooltipBodyTemplate.vue'
import { useEvent } from '@/features/events/useEvent'
import type { EventDataWithStore } from '@/types'

const props = defineProps<{
	event: EventDataWithStore
	simplified?: boolean
	readonly?: boolean
	isSelected: boolean
}>()

const { event } = toRefs(props)

const { isNonOptimal, hasEnded, isClickable, isFromCalculatedOpportunity, isFromStoreOpportunity, isPossiblyNotLegalNOAEvent } = useEvent(event)

const shouldShowWarning = computed(() => isNonOptimal.value || isPossiblyNotLegalNOAEvent.value)

const title = computed(() => {
	const suffixSegments = []

	if (isScraped.value) {
		suffixSegments.push('scheduled')
	} else if (!hasEnded.value) {
		suffixSegments.push('planned')
	} else if (isDetected.value) {
		suffixSegments.push('detected')
	}

	if (hasEnded.value) {
		suffixSegments.push('ended')
	}

	return `${props.event.name} (${suffixSegments.join(', ')})`
})

const fromOpportunityType = computed(() => {
	if (isFromCalculatedOpportunity.value) {
		return 'calculated'
	}

	if (isFromStoreOpportunity.value) {
		return 'store'
	}

	return 'none'
})

const isScraped = computed(() => event.value.event_origin === 'scraped')
const isDetected = computed(() => event.value.event_origin === 'detected')

const portal = computed(() => (props.event.storeName ? StoreToPortalMap.get(props.event.storeName) : undefined))
const portalColorVar = computed(() => ({
	'--event-portal-color': `var(--color-portal-${portal.value}-primary)`
}))
</script>

<style lang="scss" scoped>
// Intentional custom color
$opportunity-calculated-bg: #3c6166;

.discountEvent {
	color: theme('colors.ink.inverted.100');
	border-color: theme('colors.ink.87');

	&__scraped,
	&__detected {
		background-color: theme('colors.ink.87');
		color: theme('colors.ink.inverted.87');

		&:hover,
		&.is-selected {
			background-color: theme('colors.ink.DEFAULT/0.7');
		}

		&.discountEvent__opportunity-calculated {
			border-color: theme('colors.message.success');
			background-color: theme('colors.message.success');

			&:hover,
			&.is-selected {
				background-color: theme('colors.message.success/0.8');
			}
		}

		&.discountEvent__opportunity-store {
			border-color: var(--event-portal-color);
			background-color: var(--event-portal-color);
			color: theme('colors.ink.87');

			&:hover,
			&.is-selected {
				background-color: rgb(from var(--event-portal-color) r g b / 0.8);
			}
		}

		&.discountEvent__curated {
			animation: var(--linear-gradient-glow-animation);
			background: var(--linear-gradient-glow);
			background-size: 500% 100%;
		}
	}

	&__customer_created {
		background-color: theme('colors.level.05');
		color: theme('colors.ink.87');

		&:hover,
		&.is-selected {
			background-color: theme('colors.level.04');
		}

		&.discountEvent__opportunity-calculated {
			border-color: theme('colors.message.success');
			color: theme('colors.message.success');
			background-color: $opportunity-calculated-bg;

			&:hover,
			&.is-selected {
				background-color: lighten($opportunity-calculated-bg, 10%);
			}
		}

		&.discountEvent__opportunity-store {
			border-color: var(--event-portal-color);
			background-color: rgb(from var(--event-portal-color) r g b / 0.6);
			color: theme('colors.ink.87');

			&:hover,
			&.is-selected {
				background-color: rgb(from var(--event-portal-color) r g b / 0.5);
			}
		}

		&.discountEvent__curated {
			animation: var(--linear-gradient-glow-animation);
			background: linear-gradient(60deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.7) 100%), var(--linear-gradient-glow);
			background-size: 500% 100%;

			&:hover,
			&.is-selected {
				color: theme('textColor.strong');
			}
		}
	}
}
</style>
